import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9p1ehosenbniolesnug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        const isSpam = typeof serverMsg === 'string' && serverMsg.includes('spam');
        if (isSpam) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请检查网络后重试');
    } finally {
      /* keep status visible */
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 奇漫屋 - 免费韩国漫画在线阅读平台"
        description="奇漫屋致力于为中文读者提供优质免费的韩国漫画在线阅读服务。了解奇漫屋的品牌故事、发展历程和联系方式，体验最好的韩漫阅读平台。"
        keywords="奇漫屋, 关于我们, 韩漫平台, 免费漫画, 联系我们"
        canonicalUrl="https://qimanwu.com/about"
      />
      <SchemaOrg
        type="WebPage"
        pageUrl="https://qimanwu.com/about"
        pageName="关于我们 - 奇漫屋"
      />
      <SchemaOrg
        type="BreadcrumbList"
        breadcrumbItems={[
          { name: '首页', url: 'https://qimanwu.com' },
          { name: '关于我们', url: 'https://qimanwu.com/about' },
        ]}
      />
      <SchemaOrg
        type="Organization"
        data={{
          name: '奇漫屋',
          url: 'https://qimanwu.com',
          description: '奇漫屋提供免费韩国漫画大全，支持在线和下拉式阅读，随时访问最新韩漫资源。',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'contact@qimanwu.com',
            availableLanguage: ['Chinese'],
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb />

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于奇漫屋
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
                奇漫屋是一个专注于韩国漫画的免费在线阅读平台，致力于为所有韩漫爱好者提供优质、便捷、流畅的阅读体验
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
              {[
                { icon: 'ri-book-open-line', value: '1000+', label: '漫画资源' },
                { icon: 'ri-user-heart-line', value: '50万+', label: '活跃读者' },
                { icon: 'ri-timer-flash-line', value: '每日更新', label: '持续追更' },
                { icon: 'ri-shield-check-line', value: '完全免费', label: '无门槛阅读' },
              ].map((stat, i) => (
                <div key={i} className="text-center p-6 bg-background-50 border border-background-200/70 rounded-xl">
                  <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </span>
                  <div className="text-2xl font-heading font-bold text-foreground-900 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-4">
                  <p>
                    奇漫屋诞生于一群韩漫爱好者的共同梦想。作为资深韩漫读者，我们深知寻找高质量、翻译准确的韩国漫画有多不容易——散乱的资源、参差不齐的画质、频繁失效的链接，都让阅读体验大打折扣。
                  </p>
                  <p>
                    于是，在2024年，我们决定自己动手，打造一个真正为读者着想的韩漫平台。从最初的几十部漫画，到现在汇集了<strong>超过1000部优质韩漫</strong>资源，奇漫屋始终坚持<strong>免费、高清、更新快</strong>三大原则。
                  </p>
                  <p>
                    我们的团队由漫画编辑、翻译人员和开发工程师组成，致力于为每一位读者提供最好的韩漫阅读体验。无论是热门连载还是经典完结作品，恋爱甜宠还是悬疑烧脑，你都能在奇漫屋找到心仪的作品。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">用户口碑</h2>
                <div className="space-y-4">
                  {[
                    { name: '小辣椒', text: '奇漫屋是我用过最好的韩漫网站！画质清晰，加载速度快，下拉式阅读太方便了。每天都在追更，根本停不下来！', rating: 5 },
                    { name: '漫画迷小陈', text: '分类很全，想看的韩漫基本都能找到。翻译质量很高，比很多平台都好。已经推荐给身边所有朋友了。', rating: 5 },
                    { name: '深夜读书人', text: '免费还能有这么好的体验真的很良心。界面简洁干净，没有乱七八糟的广告，阅读体验很纯粹。', rating: 4 },
                  ].map((review, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-xs font-bold text-accent-700">
                          {review.name[0]}
                        </div>
                        <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                        <div className="flex items-center gap-0.5 ml-auto">
                          {Array.from({ length: 5 }).map((_, j) => (
                            <span key={j} className="w-4 h-4 flex items-center justify-center">
                              <i className={`text-xs ${j < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                            </span>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2 text-center">联系我们</h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题、建议或合作意向，欢迎通过以下表单与我们联系
              </p>

              {formStatus === 'success' ? (
                <div className="text-center py-12 bg-background-100 rounded-xl">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">发送成功！</h3>
                  <p className="text-sm text-foreground-500">感谢你的来信，我们会尽快回复。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-4 text-sm text-primary-500 cursor-pointer hover:text-primary-600"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5 bg-background-100 rounded-xl p-6 md:p-8"
                >
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    aria-hidden="true"
                    className="honeypot-field"
                  />
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      你的名字
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/80 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/80 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="about-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/80 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/80 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入你想说的话（最多500字）"
                    ></textarea>
                  </div>

                  {formStatus === 'error' && formError && (
                    <div className="text-sm text-primary-600 bg-primary-50 px-4 py-2.5 rounded-lg">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg cursor-pointer hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        发送中...
                      </span>
                    ) : (
                      '发送消息'
                    )}
                  </button>
                </form>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}